import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Progress, Row, Select, Space, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { token } from "../../functions/request";

const { Title, Text } = Typography;

const API = process.env.REACT_APP_API_RB;

// Mapeia tipo de evento SSE → label legível
const STEP_LABELS = {
  maps:             "Baixando tabelas de referência MDIC...",
  "maps-done":      "Tabelas de referência prontas",
  delete:           null, // usa message do evento
  "delete-raw":     null,
  download:         null,
  "download-done":  null,
  "insert-raw":     null,
  "insert-raw-done":null,
  "migrate-start":  "Iniciando migração para comexstat_dados...",
};

export default function ComexRawImport() {
  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: currentYear - 1997 + 1 }, (_, i) => 1997 + i).reverse(),
    [currentYear]
  );

  const [year, setYear] = useState(currentYear);
  const [status, setStatus] = useState("idle"); // idle | running | done | error
  const [stepMsg, setStepMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const readerRef = useRef(null);

  const handleMigrate = async () => {
    setStatus("running");
    setProgress(0);
    setStepMsg("Conectando...");
    setSummary(null);
    setErrorMsg("");

    const url = `${API}/comex-raw-import/migrate-stream?year=${year}&replaceYear=true`;

    try {
      const response = await fetch(url, {
        headers: { "x-access-token": token },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop(); // guarda fragmento incompleto

        for (const chunk of chunks) {
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          let event;
          try { event = JSON.parse(dataLine.slice(6)); } catch { continue; }

          if (event.type === "progress") {
            setProgress(event.pct);
            setStepMsg(`Migrando: ${event.inserted.toLocaleString()} / ${event.total.toLocaleString()} linhas (${event.pct}%)`);
          } else if (event.type === "done") {
            setProgress(100);
            setStatus("done");
            setSummary(event);
            setStepMsg("Migração concluída");
          } else if (event.type === "error") {
            setStatus("error");
            setErrorMsg(event.message);
          } else if (event.type === "migrate-start") {
            setStepMsg(`Iniciando migração: ${event.total.toLocaleString()} linhas → comexstat_dados`);
          } else if (event.message) {
            setStepMsg(STEP_LABELS[event.step] ?? event.message);
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setStatus("error");
        setErrorMsg(err.message);
      }
    }
  };

  const handleCancel = () => {
    try { readerRef.current?.cancel(); } catch (_) {}
    setStatus("idle");
    setProgress(0);
    setStepMsg("");
  };

  const isRunning = status === "running";

  return (
    <Content style={{ padding: "24px" }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={14}>
          <Card>
            <Title level={4}><SyncOutlined /> Migrar para comexstat_dados</Title>
            <Text type="secondary">
              Baixa o CSV mais recente do MDIC, insere em <strong>COMEXSTAT_NCM_RAW</strong> e
              migra para <strong>comexstat_dados</strong> com enriquecimento de países, URFs e CUCI.
              Os dados existentes do ano serão substituídos.
            </Text>

            <div style={{ marginTop: 16, maxWidth: 280 }}>
              <Text strong>Ano</Text>
              <Select
                style={{ width: "100%", marginTop: 8 }}
                value={year}
                onChange={(v) => { setYear(v); setStatus("idle"); }}
                options={years.map((y) => ({ value: y, label: String(y) }))}
                disabled={isRunning}
              />
            </div>

            <Space style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={isRunning ? <LoadingOutlined /> : <SyncOutlined />}
                onClick={handleMigrate}
                disabled={isRunning}
                loading={isRunning}
                style={{ background: "#722ed1", borderColor: "#722ed1" }}
              >
                {isRunning ? "Migrando..." : "Migrar para comexstat_dados"}
              </Button>
              {isRunning && (
                <Button danger onClick={handleCancel}>Cancelar</Button>
              )}
            </Space>

            {(isRunning || status === "done") && (
              <div style={{ marginTop: 20 }}>
                <Progress
                  percent={progress}
                  status={status === "error" ? "exception" : status === "done" ? "success" : "active"}
                  strokeColor={status === "done" ? "#52c41a" : "#722ed1"}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>{stepMsg}</Text>
              </div>
            )}

            {status === "done" && summary && (
              <Alert
                style={{ marginTop: 16 }}
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                message="Migração concluída"
                description={
                  `${summary.totalInserted?.toLocaleString()} linhas inseridas em comexstat_dados para ${summary.year}.` +
                  (summary.batchesWithError > 0 ? ` ⚠️ ${summary.batchesWithError} batch(es) com erro.` : "")
                }
              />
            )}

            {status === "error" && (
              <Alert
                style={{ marginTop: 16 }}
                type="error"
                showIcon
                icon={<CloseCircleOutlined />}
                message="Falha na migração"
                description={errorMsg}
              />
            )}
          </Card>
        </Col>
      </Row>
    </Content>
  );
}
